/**
 * modal-shell controller (vanilla-jq).
 * Open:  any element with [data-fr-modal-open="<modal-id>"]
 * Close: [data-fr-modal-close] (× button + backdrop) or ESC.
 * Adds scroll-lock, focus trap, focus restore, and a scroll cue on overflow.
 *
 * @param {*} _s The vanilla-jq helper (window._s).
 */
( function ( _s ) {
	const FOCUSABLE =
		'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

	// A STACK, not a single remembered trigger. Modals open each other — the contact and
	// request forms both link to the privacy modal from their legal line — and with one
	// `lastTrigger` + a "first .is-open in the DOM" ESC handler, pressing ESC closed
	// whichever modal happened to come first in the footer markup (contact), leaving the
	// one you were actually looking at (privacy) on screen, and released the body
	// scroll-lock while a modal was still up. Each entry remembers its own opener so
	// focus returns to the right place as the stack unwinds.
	const stack = [];

	/**
	 * The modal on top of the stack, if any.
	 *
	 * @return {HTMLElement|null} The top-most open modal.
	 */
	function topModal() {
		return stack.length ? stack[ stack.length - 1 ].modal : null;
	}

	function openModal( id ) {
		const modal = document.getElementById( id );
		if ( ! modal || ! modal.classList.contains( 'fr-modal' ) ) {
			return;
		}
		// Re-opening one that's already stacked would double its entry and strand the
		// scroll-lock; bring it to the top instead.
		if ( stack.some( entry => entry.modal === modal ) ) {
			return;
		}
		stack.push( { modal, trigger: modal.ownerDocument.activeElement } );
		// Each level sits above the one below it. Same-z-index siblings fall back to DOM
		// order, which is not the open order.
		modal.style.zIndex = String( 1000 + stack.length * 10 );
		modal.classList.add( 'is-open' ); // display it (still opacity:0 via Bootstrap .fade)
		modal.setAttribute( 'aria-hidden', 'false' );
		document.body.classList.add( 'fr-modal-open' );

		// Bootstrap's fade technique: force a reflow between "displayed" and the
		// `.show` class so the .fade opacity + panel transform actually transition
		// (a transition can't run straight out of display:none).
		void modal.offsetWidth;
		modal.classList.add( 'show' );

		const panel = modal.querySelector( '.fr-modal__panel' );
		if ( panel ) {
			// The BODY is the scroll container now (the panel pins the header), so the cue
			// tracks the body's overflow — and follows it as the user scrolls, instead of
			// being decided once at open time and then left showing forever.
			updateScrollCue( panel );

			const body = panel.querySelector( '.fr-modal__body' );
			if ( body && ! body.dataset.frCueBound ) {
				body.dataset.frCueBound = '1';
				body.addEventListener( 'scroll', () => updateScrollCue( panel ) );
			}

			const focusable = panel.querySelectorAll( FOCUSABLE );
			( focusable[ 0 ] || panel ).focus();
		}
	}

	/**
	 * Show the scroll cue only while the modal body has more to scroll.
	 *
	 * @param {HTMLElement} panel The modal panel.
	 * @return {void}
	 */
	function updateScrollCue( panel ) {
		const body = panel.querySelector( '.fr-modal__body' );

		if ( ! body ) {
			return;
		}

		const overflows = body.scrollHeight > body.clientHeight + 4;
		const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 4;

		panel.classList.toggle( 'is-overflowing', overflows );
		panel.classList.toggle( 'is-at-end', atEnd );
	}

	function closeModal( modal ) {
		if ( ! modal ) {
			return;
		}
		modal.classList.remove( 'show' ); // fade out via Bootstrap .fade
		modal.setAttribute( 'aria-hidden', 'true' );

		// Pop this modal wherever it sits in the stack (× / backdrop can close one that
		// isn't on top), and keep the body locked while anything is still open.
		const index = stack.findIndex( entry => entry.modal === modal );
		const entry = index > -1 ? stack.splice( index, 1 )[ 0 ] : null;

		if ( ! stack.length ) {
			document.body.classList.remove( 'fr-modal-open' );
		}

		// Drop display:none only AFTER the fade-out. transitionend is the happy path;
		// a timeout is the fallback for reduced-motion (no transition fires) and any
		// missed event, so the modal can't get stuck displayed.
		const reduce =
			window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		const hide = () => modal.classList.remove( 'is-open' );
		if ( reduce ) {
			hide();
		} else {
			let done = false;
			const finish = () => {
				if ( done ) {
					return;
				}
				done = true;
				modal.removeEventListener( 'transitionend', onEnd );
				hide();
			};
			const onEnd = e => {
				if ( e.target === modal ) {
					finish();
				}
			};
			modal.addEventListener( 'transitionend', onEnd );
			setTimeout( finish, 300 );
		}

		// Hand focus back to whatever opened THIS modal — for a stacked modal that is the
		// link inside its parent, not the page trigger that started the chain.
		if ( entry && entry.trigger && typeof entry.trigger.focus === 'function' ) {
			entry.trigger.focus();
		}
	}

	function trapFocus( modal, e ) {
		if ( e.key !== 'Tab' ) {
			return;
		}
		const focusable = Array.from( modal.querySelectorAll( FOCUSABLE ) ).filter(
			el => el.offsetParent !== null
		);
		if ( ! focusable.length ) {
			return;
		}
		const first = focusable[ 0 ];
		const last = focusable[ focusable.length - 1 ];
		// `modal.ownerDocument` rather than the `document` global — same node, but it keeps
		// the focus check tied to the modal's own document (and satisfies the lint rule
		// this file was already failing).
		const active = modal.ownerDocument.activeElement;

		if ( e.shiftKey && active === first ) {
			e.preventDefault();
			last.focus();
		} else if ( ! e.shiftKey && active === last ) {
			e.preventDefault();
			first.focus();
		}
	}

	_s( document ).ready( function () {
		// Open from the URL fragment on arrival — the same rule as the click handler
		// below, applied at load, so a modal can be linked to from ANOTHER page and not
		// just from a trigger on this one. Invite links rely on it
		// (/member-access/?ref=…#request-modal): without this a colleague would land on
		// the login page with their prefilled request form sitting closed and unhinted.
		// Scoped to `.fr-modal` ids, so an ordinary in-page anchor still just scrolls.
		if ( window.location.hash.length > 1 ) {
			const hashed = document.getElementById( window.location.hash.slice( 1 ) );

			if ( hashed && hashed.classList.contains( 'fr-modal' ) ) {
				openModal( hashed.id );
			}
		}

		// Auto-open a modal the server flagged for this render. A POST can't carry a URL
		// hash, so after a change-password submit re-renders the profile page (PMPro
		// processes it on `init`), a hidden [data-fr-autoopen="<modal-id>"] marker tells us
		// to re-open the modal it was submitted from, so PMPro's result shows there rather
		// than on a page the member never opened.
		const autoOpen = document.querySelector( '[data-fr-autoopen]' );
		if ( autoOpen ) {
			const autoId = autoOpen.getAttribute( 'data-fr-autoopen' );
			const autoTarget = autoId && document.getElementById( autoId );
			if ( autoTarget && autoTarget.classList.contains( 'fr-modal' ) ) {
				openModal( autoId );
			}
		}

		// Open triggers
		document.querySelectorAll( '[data-fr-modal-open]' ).forEach( function ( trigger ) {
			trigger.addEventListener( 'click', function ( e ) {
				e.preventDefault();
				openModal( trigger.getAttribute( 'data-fr-modal-open' ) );
			} );
		} );

		// Also open from a link whose href is #<modal-id> (delegated, so it works for
		// WP nav/footer menu items — a Custom Link "#contact-modal" opens the modal
		// without needing a data attribute on the client-managed menu markup).
		document.addEventListener( 'click', function ( e ) {
			const link = e.target.closest && e.target.closest( 'a[href^="#"]' );
			if ( ! link ) {
				return;
			}
			const id = link.getAttribute( 'href' ).slice( 1 );
			const modal = id && document.getElementById( id );
			if ( modal && modal.classList.contains( 'fr-modal' ) ) {
				e.preventDefault();
				openModal( id );
			}
		} );

		// Close handlers per modal
		document.querySelectorAll( '.fr-modal' ).forEach( function ( modal ) {
			modal.querySelectorAll( '[data-fr-modal-close]' ).forEach( function ( el ) {
				el.addEventListener( 'click', function () {
					closeModal( modal );
				} );
			} );
			modal.addEventListener( 'keydown', function ( e ) {
				trapFocus( modal, e );
			} );
		} );

		// ESC closes the open modal
		document.addEventListener( 'keydown', function ( e ) {
			if ( e.key === 'Escape' ) {
				// The top of the stack — NOT the first `.is-open` in the DOM, which is
				// document order and closed the wrong modal.
				const open = topModal();
				if ( open ) {
					closeModal( open );
				}
			}
		} );
	} );

	// expose for programmatic use
	window.frOpenModal = openModal;
} )( window._s );
